import { useAvailableCalendarEvents } from '@gik/api/calendar/calendar';
import { useCalendarEventTypes } from '@gik/api/calendar/calendarEventType';
import {
  ICalendarDay,
  ICalendarEntry,
  ICalendarEvent,
  ResolveGiftCardClaimConflictValues,
} from '@gik/calendar/models/Calendar';
import { useCalendarStore } from '@gik/calendar/store/CalendarStore';
import { OrderClaimStatus, summaryFormId } from '@gik/checkout';
import { Analytics } from '@gik/core/utils';
import { useBemCN } from '@gik/core/utils/bemBlock';
import sleep from '@gik/core/utils/sleep';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { dateFormat } from '@gik/l10n';
import { Button, HTMLParser, LoadingSpinner, UI } from '@gik/ui';
import moment from 'moment';
import React from 'react';
import { useEffect } from 'react';
import ReactDOM from 'react-dom';
import CalendarDay from '../Calendar/CalendarDay';
import CalendarClaimHeader from './CalendarClaimHeader';
import { translationKeys } from '../../i18n/en';
import { useTranslation } from 'react-i18next';
import ModalButtonFooter from '@gik/ui/Modal/ModalButtonFooter';
import { createClaim } from '@gik/api/calendar/calendarClaims';
import { StatusCodes } from 'http-status-codes';
import { calendarEventClaimFormContentId } from '@gik/calendar/utils/CalendarModals';
import ConflictAlreadyPaidIcon from '@gik/calendar/assets/ClaimIcons/conflictAlreadyPaid.svg';
import ConflictOptionsAvailableIcon from '@gik/calendar/assets/ClaimIcons/conflictOptionsAvailable.svg';
import noop from '@gik/core/utils/noop';

export type GiftCardClaimConflictResolutionProps = {
  resolveGiftCardClaimConflictValues: ResolveGiftCardClaimConflictValues;
  alreadyClaimedEvent: ICalendarEvent;
  alreadyClaimedEntry: ICalendarEntry;
  setSelectedEvent?(event: ICalendarEvent);
  setSelectedEntry?(entry: ICalendarEntry);
  buttonsPortal?: HTMLElement;
  onAttachClaim?(): void;
};

function fireAnalyticsEvent(
  hasAvailableOptions: boolean,
  hasPaid: boolean,
  eventName: 'ViewClaimConflict' | 'ResolveClaimConflict',
  resolveStatus?: 'cancel' | 'sendAnyway' | 'doNotAttach' | 'accept' | 'chosenAnotherRequest'
) {
  Analytics.fireEvent(
    eventName,
    {
      hasAvailableOptions: hasAvailableOptions.toString(),
      hasPaid: hasPaid.toString(),
      ...(resolveStatus ? { resolveStatus } : {}),
    },
    () => ['hasPaid'],
    keys => keys.filter(key => key !== 'hasPaid')
  );
}

export function GiftCardClaimConflictResolution({
  resolveGiftCardClaimConflictValues,
  alreadyClaimedEvent,
  alreadyClaimedEntry,
  buttonsPortal,
  setSelectedEvent,
  setSelectedEntry,
  onAttachClaim = noop,
}: GiftCardClaimConflictResolutionProps) {
  const bem = useBemCN('gift-card-claim-conflict-resolution');
  const { t } = useTranslation();

  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const setGiftCardClaimConflictResolution = useCalendarStore(state => state.setGiftCardClaimConflictResolution);
  const setPaymentConfirmationValues = useCalendarStore(state => state.setPaymentConfirmationValues);
  const setClaimFailErrorCode = useCalendarStore(state => state.setClaimFailErrorCode);
  const setIgnoreClaimConflict = useCalendarStore(state => state.setIgnoreClaimConflict);
  const { data: calendarEventTypes } = useCalendarEventTypes();
  const eventType = calendarEventTypes?.find(item => item.id === alreadyClaimedEntry.typeId);
  const [isClaiming, setIsClaiming] = React.useState(false);

  const { data: calendarEvents, isValidating: isLoadingCalendarEvents, revalidate } = useAvailableCalendarEvents(
    inkindRouteId,
    alreadyClaimedEntry.typeId
  );

  const isLoading = !calendarEvents || isLoadingCalendarEvents || !calendarEventTypes;

  const hasAvailableOptions = calendarEvents?.events.length > 0;
  const hasPaid = !!resolveGiftCardClaimConflictValues.paymentConfirmation;

  useEffect(() => {
    if (!isLoading) {
      fireAnalyticsEvent(hasAvailableOptions, hasPaid, 'ViewClaimConflict');
      // scroll the modal content to the top to see the conflict message
      setTimeout(() => {
        document.getElementById(calendarEventClaimFormContentId)?.scrollTo(0, 0);
      }, 100);
    }
  }, [isLoading, hasAvailableOptions, hasPaid]);

  async function executePurchase() {
    await sleep(500); // wait for the conflict resolution page to disappear
    document.getElementById(summaryFormId)?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  }

  async function handleClaimEvent(event: ICalendarEvent, entry: ICalendarEntry) {
    fireAnalyticsEvent(hasAvailableOptions, hasPaid, 'ResolveClaimConflict', 'chosenAnotherRequest');

    if (hasPaid) {
      setIsClaiming(true);
      const createClaimTestResponse = await createClaim({
        ...resolveGiftCardClaimConflictValues.checkoutFormPayload.createClaimRequest,
        calendarEntryId: entry.id,
        claimDate: event.startsAt,
        orderId: resolveGiftCardClaimConflictValues.paymentConfirmation.orderId,
        orderKey: resolveGiftCardClaimConflictValues.paymentConfirmation.orderKey,
      });

      if (createClaimTestResponse.status === StatusCodes.CREATED) {
        setClaimFailErrorCode(null);
        setSelectedEvent(event);
        setSelectedEntry(entry);
        setPaymentConfirmationValues({
          ...resolveGiftCardClaimConflictValues.paymentConfirmation,
          claimErrorMessage: null,
          claimStatus: OrderClaimStatus.Success,
        });
        setGiftCardClaimConflictResolution(null);
        onAttachClaim();
      } else {
        UI.error('It looks like the request you selected is no longer available. Please try again.');
        await revalidate();
      }
      setIsClaiming(false);
    } else {
      setGiftCardClaimConflictResolution(null);
      setSelectedEvent(event);
      setSelectedEntry(entry);
      executePurchase();
    }
  }

  const dataByDays: ICalendarDay[] = calendarEvents
    ? Array.from(
        calendarEvents.events
          .sort((event1, event2) => (moment.utc(event1.startsAt).isBefore(event2.startsAt) ? -1 : 1))
          .reduce((entryMap, e) => {
            const date = moment.utc(e.startsAt).startOf('day').toISOString();
            return entryMap.set(date, [...(entryMap.get(date) || []), e]);
          }, new Map<string, ICalendarEvent[]>())
          .entries()
      )?.map(entry => ({
        date: moment.utc(entry[0]).format(dateFormat),
        events: entry[1],
      }))
    : undefined;

  const buttons = [];

  function handleCancel() {
    fireAnalyticsEvent(hasAvailableOptions, hasPaid, 'ResolveClaimConflict', 'cancel');
    UI.closeDialog();
  }
  function handleDone() {
    fireAnalyticsEvent(hasAvailableOptions, hasPaid, 'ResolveClaimConflict', 'accept');
    setGiftCardClaimConflictResolution(null);
  }
  function handleSendAnyway() {
    fireAnalyticsEvent(hasAvailableOptions, hasPaid, 'ResolveClaimConflict', 'sendAnyway');
    setGiftCardClaimConflictResolution(null);
    setIgnoreClaimConflict(true);
    executePurchase();
  }

  function handleDoNotAttach() {
    fireAnalyticsEvent(hasAvailableOptions, hasPaid, 'ResolveClaimConflict', 'doNotAttach');
    setPaymentConfirmationValues(resolveGiftCardClaimConflictValues.paymentConfirmation);
    setGiftCardClaimConflictResolution(null);
  }

  let alignButtons = true;
  if (!hasPaid && hasAvailableOptions) {
    buttons.push(
      <Button key="btn-order-next" variant="default" onClick={handleCancel}>
        {t(translationKeys.claimConflictCancelButton)}
      </Button>
    );
  }
  if (!hasPaid && !hasAvailableOptions) {
    buttons.push(
      <Button key="btn-order-next" variant="default" onClick={handleCancel}>
        {t(translationKeys.claimConflictDoNotSendButton)}
      </Button>
    );
    buttons.push(
      <Button key="btn-order-next" variant="primary" onClick={handleSendAnyway}>
        {t(translationKeys.claimConflictSendAnywayButton)}
      </Button>
    );
  }
  if (hasPaid && !hasAvailableOptions) {
    alignButtons = false;
    buttons.push(
      <Button key="btn-order-next" wide variant="primary" onClick={handleDone}>
        {t(translationKeys.claimConflictDoneButton)}
      </Button>
    );
  }

  if (hasPaid && hasAvailableOptions) {
    buttons.push(
      <Button key="btn-order-next" variant="default" onClick={handleDoNotAttach}>
        {t(translationKeys.claimConflictDoNotAttachButton)}
      </Button>
    );
  }

  const buttonsContainer = alignButtons ? (
    <ModalButtonFooter centeredButtons={false}>
      <div></div>
      <div>{buttons}</div>
    </ModalButtonFooter>
  ) : (
    buttons
  );

  return (!calendarEvents && isLoading) || isClaiming ? (
    <LoadingSpinner center />
  ) : (
    <div {...bem()}>
      <CalendarClaimHeader entry={alreadyClaimedEntry} eventType={eventType} event={alreadyClaimedEvent} />
      <div {...bem('message')}>
        <div {...bem('title')}>
          <div {...bem('image')}>
            {hasAvailableOptions || !hasPaid ? <ConflictOptionsAvailableIcon /> : <ConflictAlreadyPaidIcon />}
          </div>
          <h1>{t(translationKeys.claimConflictResolutionTitle)}</h1>
        </div>

        <p>
          <HTMLParser
            rawHtml={t(
              hasAvailableOptions
                ? hasPaid
                  ? translationKeys.claimConflictHasOptionsPaidDescription
                  : translationKeys.claimConflictHasOptionsUnpaidDescription
                : hasPaid
                ? translationKeys.claimConflictNoOptionsPaidDescription
                : translationKeys.claimConflictNoOptionsUnpaidDescription
            )}
          />
        </p>
      </div>
      {calendarEvents?.events.length > 0 && (
        <>
          <ul>
            {dataByDays?.map((day, index) => {
              return (
                <li key={index}>
                  <CalendarDay
                    date={moment.utc(day.date)}
                    events={day.events}
                    entries={calendarEvents.entries}
                    onClaimEvent={handleClaimEvent}
                    resolvingClaim
                  />
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/* TODO: use renderPortal function */}
      {buttonsPortal && ReactDOM.createPortal(buttonsContainer, buttonsPortal)}
      {!buttonsPortal && buttonsContainer}
    </div>
  );
}
